import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { todayISO, daysAgoISO } from '../lib/day';

/**
 * Lightweight preset range selector. Most report questions use fixed windows
 * (past year / 6m / 3m / 1.5y), and the backend anchors relative windows to the
 * latest data date, so presets map to start_date/end_date the page passes through.
 *
 * Value shape: { preset, start_date, end_date } (either may be undefined => backend default).
 * A 'custom' preset lets the user pick an explicit start/end (down to a single day)
 * to verify data against the original source.
 *
 * Phone-friendly: the preset row wraps, buttons are touch-sized, date inputs are
 * 16px (no iOS zoom). Presets are disabled until the data bounds are known so a
 * tap never silently sends "no dates" — except Vandaag/Gisteren, which are
 * calendar days and need no bounds (that is what makes them the default: the
 * page opens on the current state of the café).
 */
const PRESETS = [
  { key: 'today', label: 'Vandaag' },     // the real calendar day, not the data max
  { key: 'yesterday', label: 'Gisteren' },
  { key: 'mtd', label: 'Deze maand' },   // month-to-date, anchored to the data max
  { key: 'ytd', label: 'Dit jaar' },     // year-to-date, anchored to the data max
  { key: '3m', label: '3 mnd', months: 3 },
  { key: '6m', label: '6 mnd', months: 6 },
  { key: '12m', label: '1 jaar', months: 12 },
  { key: '18m', label: '1,5 jaar', months: 18 },
  { key: 'all', label: 'Alles', months: null },
];

const BTN = 'px-3 py-2 sm:px-2.5 sm:py-1 rounded transition-colors disabled:opacity-40';
const DAY_PRESETS = new Set(['today', 'yesterday']);

export default function DateRangeControl({ value, onChange, dataRange }) {
  const t = useT();
  const active = value?.preset || '12m';
  const isCustom = active === 'custom';
  const [showCustom, setShowCustom] = useState(isCustom);
  const ready = Boolean(dataRange?.end);

  const apply = (preset) => {
    const max = dataRange?.end;
    let start_date, end_date;
    if (preset === 'today' || preset === 'yesterday') {
      start_date = end_date = preset === 'today' ? todayISO() : daysAgoISO(1);
    } else if (max) {
      end_date = max;
      const p = PRESETS.find((x) => x.key === preset);
      if (p?.months) {
        const d = new Date(`${max}T12:00:00`);
        d.setMonth(d.getMonth() - p.months);
        start_date = d.toISOString().slice(0, 10);
      } else if (preset === 'mtd') {
        start_date = `${max.slice(0, 7)}-01`;
      } else if (preset === 'ytd') {
        start_date = `${max.slice(0, 4)}-01-01`;
      } else {
        start_date = dataRange?.start;
      }
    }
    onChange({ preset, start_date, end_date });
  };

  const customStart = isCustom ? value?.start_date : '';
  const customEnd = isCustom ? value?.end_date : '';

  const applyCustom = (start_date, end_date) => {
    if (start_date && end_date && start_date <= end_date) {
      onChange({ preset: 'custom', start_date, end_date });
    }
  };
  const onStartChange = (e) => applyCustom(e.target.value, customEnd || dataRange?.end || e.target.value);
  const onEndChange = (e) => applyCustom(customStart || dataRange?.start || e.target.value, e.target.value);

  const toggleCustom = () => {
    const next = !showCustom;
    setShowCustom(next);
    if (next && !isCustom && dataRange?.start && dataRange?.end) {
      onChange({ preset: 'custom', start_date: dataRange.start, end_date: dataRange.end });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 max-w-full">
      <div className="flex flex-wrap items-center bg-slate-100 rounded-md p-0.5 text-xs sm:text-[11px] font-medium">
        {PRESETS.map((p) => (
          <button key={p.key} onClick={() => apply(p.key)} disabled={!ready && !DAY_PRESETS.has(p.key)}
            className={`${BTN} ${active === p.key ? 'bg-white text-[#114817] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {t(p.label)}
          </button>
        ))}
        <button onClick={toggleCustom} disabled={!ready}
          className={`${BTN} ${isCustom ? 'bg-white text-[#cf9a40] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          {t('Aangepast')}
        </button>
      </div>

      {showCustom && (
        <div className="flex flex-wrap items-center gap-1 bg-slate-100 rounded-md px-2 py-1 text-xs sm:text-[11px] font-medium text-slate-600">
          <span className="text-slate-400">{t('van')}</span>
          <input type="date" value={customStart || ''} min={dataRange?.start} max={dataRange?.end} onChange={onStartChange}
            className="bg-white rounded border border-slate-200 px-1.5 py-1.5 sm:py-0.5 text-[16px] sm:text-[11px] text-[#114817] focus:outline-none focus:border-[#cf9a40]" />
          <span className="text-slate-400">{t('tot')}</span>
          <input type="date" value={customEnd || ''} min={dataRange?.start} max={dataRange?.end} onChange={onEndChange}
            className="bg-white rounded border border-slate-200 px-1.5 py-1.5 sm:py-0.5 text-[16px] sm:text-[11px] text-[#114817] focus:outline-none focus:border-[#cf9a40]" />
        </div>
      )}
    </div>
  );
}
